import * as React from "react";

import { Collapse } from "@mui/material";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";

import { isTest } from "../../../common/constants/env";

import { AmplitudeEvents } from "@/common/constants/amplitude";
import { useAmplitude } from "@/common/hooks/amplitude/useAmplitude";
import { DashboardIcon } from "@/dashboard-design-system/Icon/DashboardIcon";
import { ChevronDown, ChevronUp } from "@/icons/outlined";

interface FaqAccordionProps {
  steps: {
    title: React.ReactNode;
    content: React.ReactNode;
    amplitudeEvent?: AmplitudeEvents;
  }[];
  defaultActiveStep?: number;
  paddingX?: number;
  iconColor?: string;
}

export const FaqAccordion: React.FunctionComponent<React.PropsWithChildren<FaqAccordionProps>> = ({
  steps,
  defaultActiveStep = null,
  paddingX = 2,
  iconColor,
}) => {
  const { logAmplitudeEvent } = useAmplitude();
  const [activeStep, setActiveStep] = React.useState(defaultActiveStep);

  // Disable animation in Cypress tests to avoid timing issues
  const isCypressTest = isTest;
  const collapseTimeout = isCypressTest ? 0 : 500;

  const toggleActiveStep = React.useCallback(
    (step: number) => () => {
      const amplitudeEvent = steps[step].amplitudeEvent;
      if (amplitudeEvent) {
        logAmplitudeEvent(amplitudeEvent);
      }
      setActiveStep((activeStep) => (activeStep === step ? null : step));
    },
    [logAmplitudeEvent, steps]
  );

  if (!steps || steps.length === 0) {
    return null;
  }
  return (
    <Box
      sx={{
        width: "100%",
        display: "flex",
        flexDirection: "column",
        justifySelf: "center",
      }}
    >
      <Stack spacing={2}>
        {steps.map((step, index) => {
          return (
            <Box
              key={index}
              sx={(theme) => ({
                backgroundColor: theme.palette.surface.neutral.supershy,
                paddingY: 2,
                paddingX,
                borderRadius: 1,
                width: "100%",
                cursor: "pointer",
              })}
              onClick={toggleActiveStep(index)}
            >
              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Box>{step.title}</Box>
                <DashboardIcon size="medium" sx={iconColor ? { color: iconColor } : undefined}>
                  {index === activeStep ? <ChevronUp /> : <ChevronDown />}
                </DashboardIcon>
              </Stack>
              <Collapse in={index === activeStep} timeout={collapseTimeout} unmountOnExit>
                <Box sx={{ paddingTop: 1.3, width: "100%" }}>{step.content}</Box>
              </Collapse>
            </Box>
          );
        })}
      </Stack>
    </Box>
  );
};
